"use client";

import { useEffect, useMemo, useState } from "react";
import { getSpacetimeToken } from "@/app/actions";
import {
  connectGlaciRuntime,
  type RuntimeConnection,
} from "@/lib/glaci-runtime-client";
import {
  EMPTY_INSIGHT,
  pickPersonalInsight,
  type Insight,
} from "@/lib/glaci-insights";
import { useCurrentOrganization } from "./use-current-organization";
import { useCurrentUser } from "./use-current-user";

/** Freshness backstop: drop and re-establish the subscription on this cadence. */
const CYCLE_MS = 5 * 60_000;
const QUERIES = [
  "SELECT * FROM insight_org_stats",
  "SELECT * FROM insight_user_stats",
];

type UserInsightRow = { externalSubject: string } & Insight;

type InsightStatsRow = {
  membersServed7D: bigint;
  executions7D: bigint;
  avgRuntimeMs7D: bigint;
};

function toInsight(row: InsightStatsRow): Insight {
  return {
    membersServed: Number(row.membersServed7D),
    executions: Number(row.executions7D),
    avgRuntimeMs: Number(row.avgRuntimeMs7D),
  };
}

/**
 * Live org + personal runtime insights ("Last 7 days" card), sourced from a
 * per-org connection to the org's own Glaci runtime SpacetimeDB (not the
 * portal connection `spacetimedb/react` consumes elsewhere). The connection
 * authenticates with the caller's OpenAuth token — the runtime's
 * `insight_user_stats` view only returns rows to linked callers, so an
 * anonymous connection would pin the personal card at zero. A failed token
 * fetch still connects anonymously (org stats are anonymous-readable). No
 * `glaciRuntimeTarget` on the current org means no connection is opened at
 * all, and the hook reports zeros as final.
 */
export function useRuntimeInsights(): {
  org: Insight;
  personal: Insight;
  isReady: boolean;
} {
  const { organization } = useCurrentOrganization();
  const { user } = useCurrentUser();
  const target = organization?.glaciRuntimeTarget;
  const uri = target?.uri;
  const moduleName = target?.module;

  const [org, setOrg] = useState<Insight>(EMPTY_INSIGHT);
  const [userRows, setUserRows] = useState<UserInsightRow[]>([]);
  const [applied, setApplied] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!uri || !moduleName) return;

    setApplied(false);
    setFailed(false);
    setOrg(EMPTY_INSIGHT);
    setUserRows([]);

    let disposed = false;
    let subscription: { unsubscribe: () => void } | null = null;
    let cycleTimer: ReturnType<typeof setInterval> | null = null;

    const readRows = (conn: RuntimeConnection) => {
      const [orgRow] = [...conn.db.insight_org_stats.iter()];
      setOrg(orgRow ? toInsight(orgRow) : EMPTY_INSIGHT);
      setUserRows(
        [...conn.db.insight_user_stats.iter()].map((row) => ({
          externalSubject: row.externalSubject,
          ...toInsight(row),
        })),
      );
    };

    const subscribe = (conn: RuntimeConnection) => {
      // Establish the NEW subscription first and only unsubscribe the old
      // one once the new one is confirmed applied. The SDK refcounts rows
      // across overlapping subscriptions to the same query, so a row that's
      // matched by both stays resident the whole time — tearing the old one
      // down first would drop its refcount to zero and fire delete events,
      // flashing the cards to zero for an RTT every cycle.
      const previous = subscription;
      const next = conn
        .subscriptionBuilder()
        .onApplied(() => {
          readRows(conn);
          setApplied(true);
          if (previous) {
            try {
              // Only valid while the handle is still active; guard in case
              // it already ended some other way (e.g. a connection error).
              previous.unsubscribe();
            } catch {
              // Already ended — nothing to do.
            }
          }
        })
        .subscribe(QUERIES);
      subscription = next;
    };

    let handle: { disconnect: () => void } | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;

    // Capped exponential backoff (1s, 2s, 4s … 30s). Values on screen are
    // deliberately kept while down — stale beats flashing to zero; the next
    // successful subscription apply refreshes them.
    const scheduleReconnect = () => {
      if (disposed || reconnectTimer) return;
      const delayMs = Math.min(30_000, 1_000 * 2 ** attempt);
      attempt += 1;
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        handle?.disconnect();
        handle = null;
        open();
      }, delayMs);
    };

    const open = () => {
      getSpacetimeToken()
        .catch(() => null)
        .then((token) => {
          // The token fetch is async: the effect may have been cleaned up
          // (unmount, target change) before it resolves — don't open a
          // connection nothing will ever tear down.
          if (disposed) return;
          handle = connectGlaciRuntime(
            { uri, module: moduleName },
            {
              onConnect: (conn) => {
                // `disconnect()` below is async: a connection already in flight
                // when this effect's cleanup ran can still call onConnect
                // afterward. Bail out so we don't subscribe against — or start a
                // resubscribe interval for — a connection nothing is listening
                // to.
                if (disposed) return;
                attempt = 0;

                // Insight views have no primary key, so only insert/delete
                // callbacks are available (no onUpdate) — see the bindings'
                // insight_org_stats/insight_user_stats table schemas.
                conn.db.insight_org_stats.onInsert(() => readRows(conn));
                conn.db.insight_org_stats.onDelete(() => readRows(conn));
                conn.db.insight_user_stats.onInsert(() => readRows(conn));
                conn.db.insight_user_stats.onDelete(() => readRows(conn));

                subscribe(conn);
                cycleTimer = setInterval(() => subscribe(conn), CYCLE_MS);
              },
              onError: (err) => {
                if (disposed) return;
                console.warn("Runtime insights connection failed:", err);
                setFailed(true);
                scheduleReconnect();
              },
              onDisconnect: () => {
                if (disposed) return;
                if (cycleTimer) {
                  clearInterval(cycleTimer);
                  cycleTimer = null;
                }
                subscription = null;
                scheduleReconnect();
              },
            },
            token,
          );
        });
    };

    open();

    return () => {
      disposed = true;
      if (cycleTimer) clearInterval(cycleTimer);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      subscription = null;
      handle?.disconnect();
    };
  }, [uri, moduleName]);

  const personal = useMemo(
    () => pickPersonalInsight(userRows, user?.id),
    [userRows, user?.id],
  );

  const hasTarget = Boolean(uri && moduleName);
  return {
    org: hasTarget ? org : EMPTY_INSIGHT,
    personal: hasTarget ? personal : EMPTY_INSIGHT,
    isReady: !hasTarget || failed || applied,
  };
}
